import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import AddIssue from "components/Issues/AddIssue";
import { useGlobalSearchSuggestionsQuery, useGlobalSearchTrendingQuery, useWikipediaIssueSuggestionsInfiniteQuery } from "hooks/queries/useSearchQueries";
import LoadingSkeleton from "ui/LoadingSkeleton";
import {
  addRecentSearch,
  getRecentSearches,
  removeRecentSearch,
} from "util/recentSearchesUtils";
import {
  getRecentIssues,
  removeRecentIssue,
  type RecentIssue,
} from "util/recentIssuesUtils";
import type { WikiSuggestion } from "util/constants";
import type {
  RecentSearchEntry,
  SearchResult,
  SearchSuggestionGroups,
  TrendingSearchGroups,
} from "types/search";
import SearchDiscoveryPanel from "./SearchDiscoveryPanel";
import SearchSuggestionsPanel from "./SearchSuggestionsPanel";
import {
  getNoResultsText,
  getSearchCopy,
} from "./searchCopy";
import {
  buildResultPath,
  buildSearchRoute,
  buildSearchSectionRoute,
  getSearchLocale,
} from "./searchUtils";
import { useUser } from "util/UserContext";
import { useBodyScrollLock } from "ui/useBodyScrollLock";

interface Props {
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  fullScreen?: boolean;
  enableWikipediaSuggestions?: boolean;
  onClose?: () => void;
}

const EMPTY_SUGGESTIONS: SearchSuggestionGroups = {
  issues: [],
  posts: [],
  actors: [],
  tags: [],
};

const EMPTY_TRENDING: TrendingSearchGroups = {
  issues: [],
  posts: [],
  tags: [],
};

const normalizeIssueKey = (value: string): string => value.trim().toLowerCase();

const GlobalSearch: React.FC<Props> = ({
  placeholder,
  className = "",
  autoFocus = false,
  fullScreen = false,
  enableWikipediaSuggestions = true,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, awaitingUser, setShowAuthModal } = useUser();
  const locale = getSearchLocale(location.search);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState<RecentSearchEntry[]>([]);
  const [recentIssues, setRecentIssues] = useState<RecentIssue[]>([]);
  const [openAddIssueModal, setOpenAddIssueModal] = useState(false);
  const [prepopulateData, setPrepopulateData] = useState<{
    name: string;
    wikipediaLink: string;
    cname: string;
    subject_properties: any;
  } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const normalizedQuery = searchQuery.trim().replace(/\s+/g, " ");
  const effectivePlaceholder = placeholder || getSearchCopy(locale, "searchPlaceholder");
  useBodyScrollLock(fullScreen && showSuggestions);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(normalizedQuery);
    }, 320);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [normalizedQuery]);

  useEffect(() => {
    if (showSuggestions) {
      setRecentSearches(getRecentSearches());
      setRecentIssues(getRecentIssues());
    }
  }, [showSuggestions]);

  const suggestionsQuery = useGlobalSearchSuggestionsQuery(
    debouncedQuery,
    5,
    showSuggestions && debouncedQuery.length >= 2
  );
  const existingIssueCnames = useMemo(
    () =>
      (suggestionsQuery.data?.data.issues || [])
        .map((result) =>
          typeof result.metadata.cname === "string" && result.metadata.cname.trim().length > 0
            ? result.metadata.cname
            : result.title
        )
        .filter((value): value is string => typeof value === "string" && value.length > 0),
    [suggestionsQuery.data]
  );
  const wikipediaSuggestionsQuery = useWikipediaIssueSuggestionsInfiniteQuery(
    debouncedQuery,
    existingIssueCnames,
    5,
    enableWikipediaSuggestions && showSuggestions && debouncedQuery.length >= 2
  );

  const trendingQuery = useGlobalSearchTrendingQuery(
    4,
    showSuggestions && debouncedQuery.length === 0
  );

  const suggestionGroups = suggestionsQuery.data?.data || EMPTY_SUGGESTIONS;
  const wikipediaSuggestions = useMemo(
    () => {
      const existingIssueSet = new Set(existingIssueCnames.map(normalizeIssueKey));
      const seenWikipedia = new Set<string>();
      const rawSuggestions = enableWikipediaSuggestions
        ? wikipediaSuggestionsQuery.data?.pages.flatMap((page) => page.items) || []
        : [];

      return rawSuggestions.filter((suggestion) => {
        const suggestionKey = normalizeIssueKey(suggestion.cname || suggestion.name);

        if (existingIssueSet.has(suggestionKey) || seenWikipedia.has(suggestionKey)) {
          return false;
        }

        seenWikipedia.add(suggestionKey);
        return true;
      }) as WikiSuggestion[];
    },
    [enableWikipediaSuggestions, existingIssueCnames, wikipediaSuggestionsQuery.data]
  );
  const trendingGroups = trendingQuery.data?.data || EMPTY_TRENDING;
  const hasSuggestionResults = useMemo(
    () =>
      suggestionGroups.issues.length > 0 ||
      wikipediaSuggestions.length > 0 ||
      suggestionGroups.posts.length > 0 ||
      suggestionGroups.actors.length > 0 ||
      suggestionGroups.tags.length > 0,
    [suggestionGroups, wikipediaSuggestions]
  );

  const handleNavigateToResults = () => {
    if (normalizedQuery.length < 2) {
      return;
    }

    addRecentSearch(normalizedQuery, "all");
    navigate(buildSearchRoute(normalizedQuery, "all", locale));
    setShowSuggestions(false);
    onClose?.();
  };

  const handleSelectResult = (result: SearchResult) => {
    if (normalizedQuery.length >= 2) {
      addRecentSearch(normalizedQuery, "all");
    }

    navigate(buildResultPath(result, locale));
    setShowSuggestions(false);
    onClose?.();
  };

  const handleSelectIssueSuggestion = (suggestion: WikiSuggestion) => {
    if (awaitingUser) {
      return;
    }

    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    setPrepopulateData({
      name: suggestion.name,
      wikipediaLink: suggestion.link,
      cname: suggestion.cname,
      subject_properties: suggestion.subject_properties,
    });
    setOpenAddIssueModal(true);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedQuery("");
    setRecentSearches(getRecentSearches());
    setRecentIssues(getRecentIssues());
    setShowSuggestions(true);
    searchInputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleNavigateToResults();
            }

            if (event.key === "Escape") {
              setShowSuggestions(false);
              onClose?.();
            }
          }}
          ref={searchInputRef}
          placeholder={effectivePlaceholder}
          autoFocus={autoFocus}
          className="eoe-input w-full bg-gray-200 dark:bg-eoe-dark-input rounded-full px-2 py-2 pr-8"
        />
        {searchQuery.length > 0 && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 p-1"
            aria-label={getSearchCopy(locale, "closeSearch")}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showSuggestions ? (
        <div
          className={`search-suggestions-container flex flex-col bg-transparent ${
            fullScreen
              ? "fixed top-[60px] left-0 right-0 bottom-0 z-50 overflow-hidden"
              : "fixed top-[56px] left-[1px] right-[1px] z-50 max-h-[calc(100dvh-72px)] overflow-hidden md:w-[580px] md:mx-auto md:left-0 md:right-0"
          }`}
        >
          <ul
            className={`shadow-lg ${
              fullScreen
                ? "flex-1 overflow-y-auto"
                : "bg-eoe-bg dark:bg-eoe-dark-bg-modal flex-shrink overflow-y-auto max-h-[calc(100dvh-72px)]"
            }`}
            onScroll={(event) => {
              const target = event.currentTarget;
              const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 80;

              if (
                nearBottom &&
                enableWikipediaSuggestions &&
                wikipediaSuggestionsQuery.hasNextPage &&
                !wikipediaSuggestionsQuery.isFetchingNextPage &&
                debouncedQuery.length >= 2
              ) {
                void wikipediaSuggestionsQuery.fetchNextPage();
              }
            }}
          >
            {debouncedQuery.length === 0 ? (
              <SearchDiscoveryPanel
                locale={locale}
                recentSearches={recentSearches}
                recentIssues={recentIssues}
                trendingGroups={trendingGroups}
                onRemoveRecentSearch={(entryId) => {
                  removeRecentSearch(entryId);
                  setRecentSearches(getRecentSearches());
                }}
                onRemoveRecentIssue={(issueId) => {
                  removeRecentIssue(issueId);
                  setRecentIssues(getRecentIssues());
                }}
                onSelectRecentSearch={(entry) => {
                  navigate(buildSearchRoute(entry.query, entry.scope, locale));
                  setShowSuggestions(false);
                  onClose?.();
                }}
                onSeeAllRecentSearches={() => {
                  navigate(buildSearchSectionRoute("recent-searches", locale));
                  setShowSuggestions(false);
                  onClose?.();
                }}
                onSelectRecentIssue={(recentIssue) => {
                  navigate(`/issue/${recentIssue.cname}?locale=${locale}`);
                  setShowSuggestions(false);
                  onClose?.();
                }}
                onSeeAllRecentIssues={() => {
                  navigate(buildSearchSectionRoute("recent-issues", locale));
                  setShowSuggestions(false);
                  onClose?.();
                }}
                onSeeAllTrendingSection={(section) => {
                  navigate(buildSearchSectionRoute(`trending-${section}`, locale));
                  setShowSuggestions(false);
                  onClose?.();
                }}
                onSelectTrendingResult={handleSelectResult}
              />
            ) : suggestionsQuery.isLoading || (enableWikipediaSuggestions && wikipediaSuggestionsQuery.isLoading) ? (
              <li className="px-2 py-2">
                <LoadingSkeleton
                  avatarSize="h-9 w-9"
                  variant="entity-avatar"
                  count={5}
                />
              </li>
            ) : hasSuggestionResults ? (
              <SearchSuggestionsPanel
                groups={suggestionGroups}
                wikipediaSuggestions={wikipediaSuggestions}
                locale={locale}
                onSeeAllSection={(scope) => {
                  navigate(buildSearchRoute(searchQuery, scope, locale));
                  setShowSuggestions(false);
                  onClose?.();
                }}
                onSelectIssueSuggestion={handleSelectIssueSuggestion}
                onSelectResult={handleSelectResult}
              />
            ) : (
              <li className="px-3 py-3 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <div className="font-medium text-eoe-text dark:text-eoe-dark-text">
                  {getNoResultsText(locale, debouncedQuery)}
                </div>
                <div className="mt-1">
                  {getSearchCopy(locale, "noResultsHint")}
                </div>
              </li>
            )}
            {enableWikipediaSuggestions && wikipediaSuggestionsQuery.isFetchingNextPage ? (
              <li
                className="flex items-center justify-center gap-2 px-2 py-3 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
                role="status"
                aria-live="polite"
              >
                <Loader2
                  className="size-4 animate-spin"
                  aria-label={getSearchCopy(locale, "loading")}
                />
                <span>{getSearchCopy(locale, "loadingSuggestions")}</span>
              </li>
            ) : null}
          </ul>
          <div className="flex-shrink-0 border-none">
            <button
              className="w-full btn-secondary py-[5px] px-2 rounded-tr-none rounded-tl-none"
              onClick={() => {
                setShowSuggestions(false);
                onClose?.();
              }}
            >
              <span className="text-eoe-dark-text">
                {getSearchCopy(locale, "closeSearch")}
              </span>
            </button>
          </div>
        </div>
      ) : null}

      {openAddIssueModal && (
        <AddIssue
          open={openAddIssueModal}
          onClose={() => setOpenAddIssueModal(false)}
          prepopulateData={prepopulateData}
        />
      )}
    </div>
  );
};

export default GlobalSearch;
